import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { type CalendarDate, toCalendarDate } from "@/lib/calendarDate"

export interface CampaignSearchState {
  /**
   * The selected booking days as `YYYY-MM-DD` calendar dates (MPE-1169).
   *
   * Previously ISO instants, which shifted the day when a search was shared
   * across timezones. A booking day has no time component, so it is never
   * converted through UTC or the browser timezone.
   */
  selectedTimePeriod: CalendarDate[]
  selectedDestination: string
  selectedFormat: string
  selectedActivationWindow: string
  zoom: number
  position: { lat: number; lng: number }
  searchRadiusKm: number | null
}

const initialState: CampaignSearchState = {
  selectedTimePeriod: [],
  selectedDestination: "",
  selectedFormat: "",
  selectedActivationWindow: "",
  zoom: 10,
  // Santiago, Chile (Moogli launches there). Override per-tenant later
  // once we have a tenant home-city setting.
  position: { lat: -33.4489, lng: -70.6693 },
  searchRadiusKm: null,
}

const campaignSearchSlice = createSlice({
  name: "campaignSearch",
  initialState,
  reducers: {
    setSelectedTimePeriod: (state, action: PayloadAction<Date[]>) => {
      // The picker hands back Dates; store the LOCAL calendar day of each.
      // Never toISOString() — that converts to UTC and can shift the day.
      state.selectedTimePeriod = action.payload
        .map(toCalendarDate)
        .filter((value): value is CalendarDate => value !== undefined)
    },
    setSelectedDestination: (state, action: PayloadAction<string>) => {
      state.selectedDestination = action.payload
    },
    setSelectedFormat: (state, action: PayloadAction<string>) => {
      state.selectedFormat = action.payload
    },
    setSelectedActivationWindow: (state, action: PayloadAction<string>) => {
      state.selectedActivationWindow = action.payload
    },
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload
    },
    setPosition: (state, action: PayloadAction<{ lat: number; lng: number }>) => {
      state.position = action.payload
    },
    setSearchRadiusKm: (state, action: PayloadAction<number | null>) => {
      state.searchRadiusKm = action.payload
    },
    setViewport: (
      state,
      action: PayloadAction<{ zoom: number; position: { lat: number; lng: number }; searchRadiusKm: number | null }>,
    ) => {
      state.zoom = action.payload.zoom
      state.position = action.payload.position
      state.searchRadiusKm = action.payload.searchRadiusKm
    },
    resetSearchFilters: () => initialState,
  },
})

export const {
  setSelectedTimePeriod,
  setSelectedDestination,
  setSelectedFormat,
  setSelectedActivationWindow,
  setZoom,
  setPosition,
  setSearchRadiusKm,
  setViewport,
  resetSearchFilters,
} = campaignSearchSlice.actions

export default campaignSearchSlice.reducer

// Selectors
/**
 * The selected booking days as `YYYY-MM-DD` strings (MPE-1169).
 *
 * Returns calendar dates, not Dates: callers write them straight to the URL and
 * the search API, and only materialise a Date at the calendar component's own
 * boundary via `fromCalendarDate`.
 */
export const selectSelectedTimePeriod = (state: { campaignSearch: CampaignSearchState }): CalendarDate[] =>
  state.campaignSearch.selectedTimePeriod

export const selectSelectedDestination = (state: { campaignSearch: CampaignSearchState }) =>
  state.campaignSearch.selectedDestination

export const selectSelectedFormat = (state: { campaignSearch: CampaignSearchState }) =>
  state.campaignSearch.selectedFormat

export const selectSelectedActivationWindow = (state: { campaignSearch: CampaignSearchState }) =>
  state.campaignSearch.selectedActivationWindow

export const selectZoom = (state: { campaignSearch: CampaignSearchState }) => state.campaignSearch.zoom

export const selectPosition = (state: { campaignSearch: CampaignSearchState }) => state.campaignSearch.position

export const selectSearchRadiusKm = (state: { campaignSearch: CampaignSearchState }) =>
  state.campaignSearch.searchRadiusKm
